import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { type FC, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { AdvisorAvatar } from "src/advisor/AdvisorAvatar";
import type { AdvisorChat, ChatMessage } from "src/advisor/useAdvisorChat";

/** Indent of everything that belongs to an advisor turn: avatar (26) + gap (10). */
const TURN_INDENT = "36px";
const QUICK_REPLIES = ["metro", "aws", "applications"] as const;

const AssistantTurn: FC<{ text: string; muted?: boolean }> = ({ text, muted }) => (
	<Stack direction="row" spacing={1.25} sx={{ alignItems: "flex-start" }}>
		<AdvisorAvatar />
		<Typography
			sx={{ flex: 1, minWidth: 0, fontSize: 16, lineHeight: "22px", whiteSpace: "pre-wrap" }}
			color={muted ? "text.secondary" : "text.primary"}>
			{text}
		</Typography>
	</Stack>
);

const UserTurn: FC<{ text: string }> = ({ text }) => (
	<Box
		sx={{
			alignSelf: "flex-end",
			maxWidth: "85%",
			px: 1.75,
			py: 1.125,
			borderRadius: "16px 16px 4px 16px",
			bgcolor: "brand.userBubble",
			fontSize: 16,
			lineHeight: "20px",
			whiteSpace: "pre-wrap",
			overflowWrap: "anywhere"
		}}>
		{text}
	</Box>
);

const QuickReply: FC<{ label: string; onSend: (text: string) => void }> = ({ label, onSend }) => {
	const handleClick = useCallback(() => onSend(label), [label, onSend]);
	return (
		<ButtonBase
			onClick={handleClick}
			sx={{
				minHeight: 36,
				px: 1.5,
				borderRadius: "999px",
				border: 1,
				borderColor: "brand.outline",
				bgcolor: "background.paper",
				fontSize: 14,
				"&:hover": { borderColor: "primary.main" }
			}}>
			{label}
		</ButtonBase>
	);
};

const formatSentAt = (date: Date, locale: string) =>
	new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit" }).format(date);

const Turn: FC<{ message: ChatMessage }> = ({ message }) =>
	message.role === "user" ? <UserTurn text={message.content} /> : <AssistantTurn text={message.content} />;

/**
 * The advisor thread (Figma "Body" of the AI Advisor column / M12 Ask the Advisor): advisor turns
 * are avatar + plain text, user turns are grey bubbles on the right. Scrolls itself to the newest turn.
 */
export const AdvisorConversation: FC<{ chat: AdvisorChat }> = ({ chat }) => {
	const { t, i18n } = useTranslation();
	const { messages, busy, error, send } = chat;
	const scroller = useRef<HTMLDivElement>(null);
	const last = messages.at(-1);

	useEffect(() => {
		const element = scroller.current;
		if (element && messages.length > 0) element.scrollTop = element.scrollHeight;
	}, [messages]);

	const handleQuickReply = useCallback((text: string) => void send(text), [send]);

	return (
		<Box ref={scroller} sx={{ flex: 1, minHeight: 0, overflowY: "auto" }} aria-live="polite">
			<Stack spacing={1.75} sx={{ px: 2.5, pt: 2, pb: 3 }}>
				<Typography variant="caption" color="text.secondary" sx={{ textAlign: "center" }}>
					{t("advisor.today", { time: formatSentAt(messages[0]?.sentAt ?? new Date(), i18n.language) })}
				</Typography>
				<AssistantTurn text={t("advisor.intro")} />
				{messages.map(message => (
					<Turn key={message.id} message={message} />
				))}
				{busy && last?.role === "user" && <AssistantTurn text={t("advisor.thinking")} muted />}
				{error && (
					<Typography variant="body2" color="error" sx={{ pl: TURN_INDENT }}>
						{error}
					</Typography>
				)}
				{!busy && last?.role === "assistant" && (
					<Typography variant="caption" color="text.secondary" sx={{ pl: TURN_INDENT }}>
						{t("advisor.disclaimer")}
					</Typography>
				)}
				{messages.length === 0 && (
					<Stack direction="row" useFlexGap spacing={1} sx={{ flexWrap: "wrap", pl: TURN_INDENT }}>
						{QUICK_REPLIES.map(reply => (
							<QuickReply key={reply} label={t(`advisor.quickReplies.${reply}`)} onSend={handleQuickReply} />
						))}
					</Stack>
				)}
			</Stack>
		</Box>
	);
};
